import React from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box, Button } from '@mui/material';
import { JsxEmit } from 'typescript';
import { ReactComponent as CouponIcon } from './assets/coupon-icon.svg';
import { ReactComponent as CrownIcon } from './assets/crown-icon.svg';
import { ReactComponent as GreenTickIcon } from './assets/green-tick-icon.svg';
import { ReactComponent as StarIcon } from './assets/star-icon.svg';
import { ReactComponent as StarsIcon } from './assets/stars-icon.svg';
import { ReactComponent as ThumbIcon } from './assets/thumb-icon.svg';
import styles from './planCard.module.css';
import { numberWithCommas } from '../../../common/utils/argMinMax';
import { useAppSelector } from '../../../store/hooks';
import {
  AppParentPortalDataSlice,
  selectAppParentPortalData,
} from '../../../store/reducer/appParentPortalDataSlice';
import {
  AppStateAuthentication,
  selectAppAuthentication,
} from '../../../store/reducer/appStateSlice';
import { Plans } from '../../../view';

type CardDecoration = 'best-value' | 'current-plan' | 'none';
type CardContentEntry = {
  icon: 'none' | 'tick' | 'coupon';
  content: React.ReactNode;
};

interface IPlanCardTitle {
  icon: 'crown' | 'thumb' | 'star' | 'stars';
  title: string;
  price: number;
  pricePeriod: string;
}

export const PlanIcon = ({ icon }: { icon: IPlanCardTitle['icon'] }) => {
  return (
    <>
      {icon === 'crown' ? <CrownIcon /> : null}
      {icon === 'thumb' ? <ThumbIcon /> : null}
      {icon === 'star' ? <StarIcon /> : null}
      {icon === 'stars' ? <StarsIcon /> : null}
    </>
  );
};

export const PlanCardTitle: React.FC<IPlanCardTitle> = (props) => {
  return (
    <React.Fragment>
      <Box className={styles.planTitleBox}>
        <Box className={styles.planTitleContentBox}>
          <PlanIcon icon={props.icon} />
          <Box className={styles.planTitleContentTextBox}>
            <span className={styles.planTitleContentTitleText}>
              {props.title}
            </span>
            <span className={styles.planTitleContentSubTitleText}>
              HKD ${numberWithCommas(props.price)}
            </span>
            {/* <span className={styles.planTitleContentSubTitleText}>
                {props.pricePeriod}
              </span> */}
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
};

interface IPlanCardContent {
  content: CardContentEntry[];
  footnote: string;
}
export const PlanCardContent: React.FC<IPlanCardContent> = (props) => {
  return (
    <React.Fragment>
      <Box className={styles.planContentBox}>
        <Box className={styles.planContentListBox}>
          {props.content.map((eachLine, index) => {
            return (
              <Box className={styles.planContentRowBox} key={index}>
                {eachLine.icon === 'tick' ? (
                  <CheckCircleRoundedIcon
                    className={styles.planContentBoxIcon}
                    style={{ color: '#FF6112' }}
                  />
                ) : null}
                {eachLine.icon === 'coupon' ? (
                  <CouponIcon className={styles.planContentBoxIcon} />
                ) : null}
                <span className={styles.planContentRowText}>
                  {eachLine.content}
                </span>
              </Box>
            );
          })}
        </Box>
        <Box className={styles.planContentFootnoteBox}>
          <span className={styles.planContentFootnoteText}>
            {props.footnote}
          </span>
        </Box>
      </Box>
    </React.Fragment>
  );
};

interface IPlanCardButtons {
  buttons: React.ReactNode[];
}
export const PlanCardButtons: React.FC<IPlanCardButtons> = (props) => {
  return (
    <React.Fragment>
      <Box className={styles.buttonBox}>{props.buttons}</Box>
    </React.Fragment>
  );
};

export interface PlanCardProps {
  planName: Plans;
  decoration?: CardDecoration;
  title: IPlanCardTitle;
  content: IPlanCardContent;
  onClick: (
    authenticationData?: AppStateAuthentication,
    appParentPortalData?: AppParentPortalDataSlice,
  ) => void;
  fullWidth?: boolean;
}

export const PlanCard = (props: PlanCardProps): JSX.Element => {
  // OPTIONAL REDUX
  const authenticationData = useAppSelector(selectAppAuthentication);
  const appParentPortalData = useAppSelector(selectAppParentPortalData);

  return (
    <Box
      className={`${styles.decorationBox} ${
        props.decoration === 'best-value' ? styles.bestValue : undefined
      }
      ${props.decoration === 'current-plan' ? styles.currentPlan : undefined}
      ${props.fullWidth ? styles.fullWidth : undefined}`}
      style={
        props.decoration
          ? {}
          : {
              boxShadow: 'none',
            }
      }
    >
      {!props.fullWidth || props.decoration ? (
        <span className={styles.decorationText}>
          {!props.decoration || props.decoration === 'none'
            ? ' '
            : props.decoration === 'best-value'
            ? 'Best Value'
            : 'Current Plan'}
        </span>
      ) : undefined}

      <Box
        className={styles.mainBox}
        style={props.fullWidth ? { width: '100%' } : undefined}
      >
        <PlanCardTitle {...props.title} />
        <Box className={styles.buttonBox}>
          <button
            key="choose-plan-button"
            disabled={props.decoration === 'current-plan'}
            onClick={() => {
              props.onClick(authenticationData, appParentPortalData);
            }}
            className={styles.button}
          >
            Choose Plan
          </button>
        </Box>
        <PlanCardContent {...props.content} />
      </Box>
    </Box>
  );
};

export interface Prices {
  basePrice: number;
  fullPayPrice?: number;
  bnplPrice?: number;
}

export const planPrices: {
  [K in Plans]: Prices;
} = {
  [Plans.bblYearlySubscription]: {
    basePrice: 359,
  },
  [Plans.bblHalfYearSubscription]: {
    basePrice: 479,
  },
  [Plans.bblMonthlySubscription]: {
    basePrice: 599,
  },
  [Plans.bblProjectAlphaBiyearly]: {
    basePrice: 60990,
    fullPayPrice: 59490,
    bnplPrice: 60990,
  },
  [Plans.bblProjectAlphaYearly]: {
    basePrice: 35900,
    fullPayPrice: 34900,
    bnplPrice: 35900,
  },
  [Plans.bblProjectAlpha]: {
    basePrice: 19890,
    fullPayPrice: 18890,
    bnplPrice: 19890,
  },
};

export const basePlans: {
  [K in Plans]: PlanCardProps;
} = {
  [Plans.bblYearlySubscription]: {
    planName: Plans.bblYearlySubscription,
    decoration: 'best-value',
    title: {
      icon: 'crown',
      title: 'Yearly Plan',
      price: planPrices[Plans.bblYearlySubscription].basePrice,
      pricePeriod: 'month',
    },
    content: {
      content: [
        {
          icon: 'tick',
          content: (
            <span>
              <b>24</b> Courses and Experiment Boxes
            </span>
          ),
        },
        {
          icon: 'coupon',
          content: <span>18% Off to Big Bang Academy’s Workshops & Camps</span>,
        },
        {
          icon: 'tick',
          content: <span>Graduation Certificate</span>,
        },
      ],
      footnote: 'HKD $4,308 / year.',
    },
    onClick(): void {},
  },
  [Plans.bblHalfYearSubscription]: {
    planName: Plans.bblHalfYearSubscription,
    title: {
      icon: 'thumb',
      title: 'Six-Month Plan',
      price: planPrices[Plans.bblHalfYearSubscription].basePrice,
      pricePeriod: 'month',
    },
    content: {
      content: [
        {
          icon: 'tick',
          content: (
            <span>
              <b>12</b> Courses and Experiment Boxes
            </span>
          ),
        },
        {
          icon: 'coupon',
          content: <span>10% Off to Big Bang Academy’s Workshops & Camps</span>,
        },
      ],
      footnote: 'HKD $2,874 / six months.',
    },
    onClick(): void {},
  },
  [Plans.bblMonthlySubscription]: {
    planName: Plans.bblMonthlySubscription,
    title: {
      icon: 'star',
      title: 'Monthly Plan',
      price: planPrices[Plans.bblMonthlySubscription].basePrice,
      pricePeriod: 'month',
    },
    content: {
      content: [
        {
          icon: 'tick',
          content: (
            <span>
              <b>2</b> Courses and Experiment Boxes
            </span>
          ),
        },
        {
          icon: 'coupon',
          content: (
            <span>
              Join with HKD 269/month (first month) with the discount code
              <b style={{ color: '#C55600' }}> BBA1MONTH</b>.
            </span>
          ),
        },
      ],
      footnote: 'HKD $4,308 / year.',
    },
    onClick(): void {},
  },

  // Project Alpha plans
  [Plans.bblProjectAlphaBiyearly]: {
    planName: Plans.bblProjectAlphaBiyearly,
    title: {
      icon: 'crown',
      title: '2-Year Plan',
      price: planPrices[Plans.bblProjectAlphaBiyearly].basePrice,
      pricePeriod: 'program',
    },
    content: {
      content: [
        {
          icon: 'tick',
          content: (
            <span>
              <b>48</b> Experiments with e-Platform
            </span>
          ),
        },
        {
          icon: 'tick',
          content: (
            <span>
              <b>24</b> Physical Workshops & <b>8</b> Excursions
            </span>
          ),
        },
        {
          icon: 'tick',
          content: <span>Science Fair & Graduation Ceremony</span>,
        },
        {
          icon: 'tick',
          content: <span>Cambridge Competition & Tour Eligibility</span>,
        },
        {
          icon: 'tick',
          content: <span>Mid Term Assessment & Quarterly Report Card</span>,
        },
        {
          icon: 'tick',
          content: <span>Invitation to Exclusive VIP Events</span>,
        },
      ],
      footnote: '',
    },
    onClick(): void {},
  },
  [Plans.bblProjectAlphaYearly]: {
    planName: Plans.bblProjectAlphaYearly,
    title: {
      icon: 'star',
      title: '1-Year Plan',
      price: planPrices[Plans.bblProjectAlphaYearly].basePrice,
      pricePeriod: 'program',
    },
    content: {
      content: [
        {
          icon: 'tick',
          content: (
            <span>
              <b>24</b> Experiments with e-Platform
            </span>
          ),
        },
        {
          icon: 'tick',
          content: (
            <span>
              <b>12</b> Physical Workshops & <b>4</b> Excursions
            </span>
          ),
        },
        {
          icon: 'tick',
          content: <span>Science Fair & Graduation Ceremony</span>,
        },
        {
          icon: 'tick',
          content: <span>Cambridge Competition & Tour Eligibility</span>,
        },
        {
          icon: 'tick',
          content: <span>Mid Term Assessment & Quarterly Report Card</span>,
        },
      ],
      footnote: '',
    },
    onClick(): void {},
  },
  [Plans.bblProjectAlpha]: {
    planName: Plans.bblProjectAlpha,
    title: {
      icon: 'stars',
      title: '6-Month Plan',
      price: planPrices[Plans.bblProjectAlpha].basePrice,
      pricePeriod: 'program',
    },
    content: {
      content: [
        {
          icon: 'tick',
          content: (
            <span>
              <b>12</b> Experiments with e-Platform
            </span>
          ),
        },
        {
          icon: 'tick',
          content: (
            <span>
              <b>6</b> Physical Workshops & <b>2</b> Excursions
            </span>
          ),
        },
        {
          icon: 'tick',
          content: <span>Science Fair</span>,
        },
      ],
      footnote: '',
    },
    onClick(): void {},
  },
};
